/* eslint-disable camelcase */

import { APISavedCard, FormattedAPISavedCard } from 'js/apps/SavedCards/SavedCards.types';
import { datadogLogs } from 'root/datadog/datadogLogs';

export type PaymentMethodType = 'card' | 'us_bank_account';

export const PAYMENT_METHOD_TYPES = {
  CARD: 'card' as PaymentMethodType,
  US_BANK_ACCOUNT: 'us_bank_account' as PaymentMethodType,
};

function buildMethodsQueryString(methods?: PaymentMethodType | PaymentMethodType[]): string {
  if (!methods) return '';
  const methodsArray = Array.isArray(methods) ? methods : [methods];
  return methodsArray.map((m) => `methods[]=${encodeURIComponent(m)}`).join('&');
}

export function fetchSavedCards(
  customerId: string,
  methods?: PaymentMethodType | PaymentMethodType[]
): Promise<FormattedAPISavedCard[]> {
  const queryString = buildMethodsQueryString(methods);
  const url = queryString
    ? `/admin/customers/${customerId}/cards?${queryString}`
    : `/admin/customers/${customerId}/cards`;

  // using fetch
  return fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then((data) => {
      if (data.length === 0) {
        return [];
      }

      data.sort((a: APISavedCard, b: APISavedCard) => {
        // Default cards should be at the top
        // Expired and unavailable cards should be at the bottom
        if (a.is_expired && !b.is_expired) return 1;
        if (!a.is_expired && b.is_expired) return -1;
        if (!a.is_available && b.is_available) return 1;
        if (a.is_available && !b.is_available) return -1;
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;

        // Otherwise leave the order as is
        return 0;
      });

      // Include type field to distinguish between cards and ACH
      const formattedData = data.map((savedCard: APISavedCard) => ({
        remoteCardRef: savedCard.remote_card_ref,
        type: savedCard.type,
        brand: savedCard.brand,
        accountType: savedCard.account_type,
        last4: savedCard.last4,
        // Expired/unavailable cards are not selectable, so they cannot be default
        isExpired: savedCard.is_expired,
        isDefaultSelected: !savedCard.is_expired && savedCard.is_available && savedCard.is_default,
        isAvailable: savedCard.is_available,
        nickname: savedCard.nickname ?? null,
      }));

      return formattedData as FormattedAPISavedCard[];
    })
    .catch((error) => {
      datadogLogs.logger.error(
        `SavedCardsAPI error - Customer ID: ${customerId}`,
        {
          mesasge: error.massage || 'There was a problem with the Saved Cards API fetch operation',
        },
        error
      );
      throw error;
    });
}

export function fetchSavedCardsMock(customerId: string): Promise<FormattedAPISavedCard[]> {
  // eslint-disable-next-line no-console
  console.warn(
    'Using mock data for fetchSavedCards. This should only be used for testing purposes. Customer ID:',
    customerId
  );

  // Mock data for testing purposes
  return new Promise((resolve) => {
    setTimeout(() => {
      const mockData: APISavedCard[] = [
        {
          remote_card_ref: 'card_9010',
          type: 'card',
          last4: '9010',
          brand: 'Visa',
          is_default: false,
          is_available: true,
          is_expired: true,
        },
        {
          remote_card_ref: 'card_1234',
          type: 'card',
          last4: '1234',
          brand: 'Visa',
          is_default: true,
          is_available: true,
          is_expired: false,
        },
        {
          remote_card_ref: 'card_1010',
          type: 'card',
          last4: '1010',
          brand: 'Visa',
          is_default: false,
          is_available: false,
          is_expired: false,
        },
        {
          remote_card_ref: 'card_5678',
          type: 'card',
          last4: '5678',
          brand: 'MasterCard',
          is_default: false,
          is_available: true,
          is_expired: false,
        },
        {
          remote_card_ref: 'bank_6789',
          type: 'us_bank_account',
          last4: '6789',
          brand: null,
          account_type: 'Checking',
          is_default: false,
          is_available: true,
          is_expired: false,
        },
      ];

      if (mockData.length === 0) {
        resolve([]);
        return;
      }

      // Apply the same sorting logic as fetchSavedCards
      mockData.sort((a: APISavedCard, b: APISavedCard) => {
        // Default cards should be at the top
        // Expired and unavailable cards should be at the bottom
        if (a.is_expired && !b.is_expired) return 1;
        if (!a.is_expired && b.is_expired) return -1;
        if (!a.is_available && b.is_available) return 1;
        if (a.is_available && !b.is_available) return -1;
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;

        // Otherwise leave the order as is
        return 0;
      });

      // Apply the same transformation logic as fetchSavedCards
      const formattedData = mockData.map((savedCard: APISavedCard) => ({
        remoteCardRef: savedCard.remote_card_ref,
        type: savedCard.type,
        brand: savedCard.brand,
        accountType: savedCard.account_type,
        last4: savedCard.last4,
        // Expired/unavailable cards are not selectable, so they cannot be default
        isExpired: savedCard.is_expired,
        isDefaultSelected: !savedCard.is_expired && savedCard.is_available && savedCard.is_default,
        isAvailable: savedCard.is_available,
      }));

      resolve(formattedData as FormattedAPISavedCard[]);
    }, 1000);
  });
}
